// Phone sections: on a phone a page shows one section at a time under a row of tabs, so it never
// scrolls. On wider screens the tabs are hidden and every section shows in place (display: contents),
// so the page's own layout is unchanged.

import { esc } from './fmt.ts';

const active = new Map<string, string>();

export interface PSection { key: string; title: string; html: string; dot?: boolean }

/** The open section of page `id` among `keys` (the first unless one was picked). */
function openOf(id: string, keys: string[], pick?: string): string {
  if (pick && !active.has(id) && keys.includes(pick)) active.set(id, pick);
  return keys.includes(active.get(id) ?? '') ? active.get(id)! : keys[0];
}

/**
 * Just the tab row, for a page whose sections sit apart (wrap each with psecWrap).
 * all: the sections take turns at every size, not just on a phone.
 */
export function psecTabs(id: string, secs: { key: string; title: string; dot?: boolean }[], pick?: string, all = false): string {
  if (secs.length < 2) return '';
  const on = openOf(id, secs.map((s) => s.key), pick);
  return `<nav class="psec-tabs${all ? ' all' : ''}" data-key="psec-${id}" style="--n:${secs.length}">${secs.map((s) => `<button class="${s.key === on ? 'on' : ''}" data-act="psec" data-id="${id}:${s.key}" aria-pressed="${s.key === on}">${esc(s.title)}${s.dot ? '<i class="dot"></i>' : ''}</button>`).join('')}</nav>`;
}

/** One section of page `id`, shown on a phone only while its tab is open. part: tells apart two pieces of one section. */
export function psecWrap(id: string, key: string, keys: string[], html: string, all = false, part = ''): string {
  return `<div class="psec${all ? ' all' : ''}${openOf(id, keys) === key ? ' on' : ''}" data-key="psec-${id}-${key}${part ? `-${part}` : ''}">${html}</div>`;
}

/** The sections of page `id`, the open one first chosen by `pick` (else the first). Empty sections are left out. */
export function phoneSections(id: string, secs: PSection[], pick?: string, all = false): string {
  const shown = secs.filter((s) => s.html);
  if (!shown.length) return '';
  const keys = shown.map((s) => s.key);
  return `${psecTabs(id, shown, pick, all)}${shown.map((s) => psecWrap(id, s.key, keys, s.html, all)).join('')}`;
}

/** A click on a section tab ("page:key"); true when it was one. */
export function psecClick(act: string, id: string): boolean {
  if (act !== 'psec') return false;
  const [page, key] = id.split(':');
  active.set(page, key);
  return true;
}

/** Open a section from code, as when an action's result shows in another section. */
export function openSection(page: string, key: string) { active.set(page, key); }
